// Mobile list screen: a header (back, name, where it lives, share), the view picker, and the tasks
// grouped like the view (status by default) with subtasks folded under their parent. Board, calendar
// and overview views use the desktop components. A bottom bar searches and adds tasks.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { CaretRight, ChevronDown, Folder, Layers, ListTodo, Plus, Search, UserCheck, X } from '@/components/ui/icons';
import type { Location, Task, View } from '@shared/types';
import { defaultViewSettings, VIEW_TYPE_META } from '@shared/constants';
import { useDb, useMe } from '@/lib/store';
import { locationPath, useOpenTask, useWorkspaceId } from '@/lib/nav';
import { effectiveStatuses, listIdsIn, locationName, statusOf } from '@/lib/hierarchy';
import { runQuery, type TaskGroup } from '@/lib/query';
import { createTask } from '@/lib/actions';
import { pushRecent, toggleExpanded, useUi } from '@/lib/ui';
import { useIsGuest } from '@/lib/roles';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { Icon } from '@/components/ui/Icon';
import { StatusPill } from '@/components/fields/Status';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { ShareButton } from '@/components/ShareButton';
import { BoardView } from '@/views/BoardView';
import { CalendarView } from '@/views/CalendarView';
import { OverviewView } from '@/views/OverviewView';
import { TaskStatusIcon } from '@/pages/inbox/InboxRow';
import { MobileHeader } from './MobileShell';

function TaskRow({ task, kids, depth, children }: { task: Task; kids: Task[]; depth: number; children: Map<string, Task[]> }) {
  const db = useDb((d) => d);
  const openTask = useOpenTask();
  const open = useUi((s) => s.expanded[`m:t:${task.id}`] ?? false);
  const status = statusOf(db, task);
  return (
    <>
      <div className="m-lrow" style={{ paddingLeft: 8 + depth * 22 }}>
        <button type="button" className={clsx('m-lrow-caret', { hidden: !kids.length, open })} onClick={() => toggleExpanded(`m:t:${task.id}`, !open)} aria-label={open ? 'Hide subtasks' : 'Show subtasks'}>
          <CaretRight size={12} />
        </button>
        <button type="button" className="m-lrow-main" onClick={() => openTask(task.id)}>
          <TaskStatusIcon db={db} task={task} size={18} />
          <span className="m-lrow-name truncate">{task.name}</span>
          {kids.length > 0 && <span className="m-lrow-kids">{kids.length}</span>}
        </button>
        <span className="m-lrow-status">
          <StatusPill status={status} statuses={effectiveStatuses(db, task.listId)} />
        </span>
      </div>
      {open && kids.map((k) => <TaskRow key={k.id} task={k} kids={children.get(k.id) ?? []} depth={depth + 1} children={children} />)}
    </>
  );
}

function AddTask({ group, listId }: { group: TaskGroup; listId: string | undefined }) {
  const [name, setName] = useState<string | null>(null);
  if (!listId) return null;
  const add = () => {
    const n = (name ?? '').trim();
    if (n) createTask({ listId, name: n, ...(group.defaults as object) });
    setName(n ? '' : null);
  };
  if (name === null)
    return (
      <button type="button" className="m-ladd" onClick={() => setName('')}>
        <Plus size={16} /> Add Task
      </button>
    );
  return (
    <div className="m-ladd editing">
      <input
        autoFocus
        value={name}
        placeholder="Task name"
        onChange={(e) => setName(e.target.value)}
        onBlur={add}
        onKeyDown={(e) => {
          if (e.key === 'Enter') add();
          if (e.key === 'Escape') setName(null);
        }}
      />
    </div>
  );
}

function TaskList({ location, view, search, meOnly }: { location: Location; view: View | null; search: string; meOnly: boolean }) {
  const me = useMe();
  const db = useDb((d) => d);
  const settings = view?.settings ?? defaultViewSettings('list');
  const result = useMemo(() => runQuery(db, location, { ...settings, meMode: settings.meMode || meOnly }, { meId: me, search }), [db, location, settings, me, search, meOnly]);
  const firstList = useMemo(() => (location.type === 'list' ? location.id : [...listIdsIn(db, location)][0]), [db, location]);
  const collapsed = useUi((s) => s.expanded);
  return (
    <div className="m-scroll m-ltasks">
      {result.groups.map((g) => {
        const shut = collapsed[`m:g:${location.id}:${g.key}`] ?? false;
        return (
          <section key={g.key} className="m-lgroup">
            <button type="button" className="m-lgroup-head" onClick={() => toggleExpanded(`m:g:${location.id}:${g.key}`, !shut)}>
              <ChevronDown size={16} className={clsx('m-lgroup-chev', { shut })} />
              {g.status ? (
                <StatusPill status={g.status} statuses={g.status ? [g.status] : []} />
              ) : (
                <span className="m-lgroup-label">
                  {g.icon && <Icon name={g.icon} size={16} />}
                  {g.label}
                </span>
              )}
              <span className="m-lgroup-count">{g.tasks.length}</span>
            </button>
            {!shut && (
              <>
                <div className="m-lcols">
                  <span>Name</span>
                  <span>Status</span>
                </div>
                {g.tasks.map((t) => (
                  <TaskRow key={t.id} task={t} kids={result.children.get(t.id) ?? []} depth={0} children={result.children} />
                ))}
                <AddTask group={g} listId={firstList} />
              </>
            )}
          </section>
        );
      })}
      {result.total === 0 && (
        <div className="m-empty">
          <strong>{search ? 'No matching tasks' : 'No tasks yet'}</strong>
          <span>{search ? 'Try another search.' : 'Tap + to add one.'}</span>
        </div>
      )}
    </div>
  );
}

export function MobileLocation({ location, viewId }: { location: Location; viewId?: string }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const guest = useIsGuest();
  const db = useDb((d) => d);
  const [search, setSearch] = useState<string | null>(null);
  const [meOnly, setMeOnly] = useState(false);
  const views = useMemo(
    () =>
      Object.values(db.views)
        .filter((v) => v.location.type === location.type && v.location.id === location.id && v.type !== 'doc')
        .sort((a, b) => a.orderIndex - b.orderIndex),
    [db.views, location],
  );
  const view = views.find((v) => v.id === viewId) ?? (viewId === 'tasks' ? null : (views.find((v) => v.type === 'list') ?? views[0] ?? null));
  const exists = location.type === 'workspace' ? !!db.workspaces[location.id] : location.type === 'list' ? !!db.lists[location.id] : location.type === 'folder' ? !!db.folders[location.id] : !!db.spaces[location.id];

  useEffect(() => {
    if (exists && location.type !== 'workspace') pushRecent({ kind: 'location', id: location.id, location });
  }, [exists, location]);

  if (!exists)
    return (
      <div className="m-page">
        <MobileHeader title="Not found" />
        <div className="m-empty">
          <strong>This isn’t available</strong>
          <span>It may have been deleted, or it isn’t shared with you.</span>
        </div>
      </div>
    );

  const list = location.type === 'list' ? db.lists[location.id] : undefined;
  const sub =
    location.type === 'list'
      ? `in ${list?.folderId ? (db.folders[list.folderId]?.name ?? '') : (db.spaces[list?.spaceId ?? '']?.name ?? '')}`
      : location.type === 'folder'
        ? `in ${db.spaces[db.folders[location.id]?.spaceId ?? '']?.name ?? ''}`
        : location.type === 'space'
          ? 'All tasks in this space'
          : 'Everything in this workspace';
  const icon =
    location.type === 'list' ? (
      <ListTodo size={22} color={list?.color ?? undefined} />
    ) : location.type === 'folder' ? (
      <Folder size={22} />
    ) : location.type === 'space' ? (
      <SpaceAvatar spaceId={location.id} size={30} />
    ) : (
      <Layers size={22} />
    );
  const kind = view?.type ?? 'list';
  const meta = VIEW_TYPE_META[kind];
  const goView = (v: View | null) => navigate(locationPath(ws, location, v?.id ?? 'tasks'), { replace: true });

  return (
    <div className="m-page m-location">
      <MobileHeader
        icon={icon}
        title={location.type === 'workspace' ? 'All Tasks' : locationName(db, location)}
        sub={sub}
        actions={location.type === 'list' || location.type === 'folder' ? <ShareButton target={location as never} link={() => window.location.href} className="m-icon-btn m-share" iconSize={20} /> : undefined}
      />
      <div className="m-ltabs">
        <Popover
          content={(close) => (
            <Menu style={{ width: 240 }}>
              {views.map((v) => (
                <MenuItem key={v.id} icon={<Icon name={VIEW_TYPE_META[v.type].icon} size={15} />} label={v.name} checked={v.id === view?.id} onClick={() => (close(), goView(v))} />
              ))}
              {views.length === 0 && <MenuItem icon={<Icon name="List" size={15} />} label="List" checked onClick={close} />}
            </Menu>
          )}
        >
          <button type="button" className="m-ltab">
            <Icon name={meta.icon} size={16} />
            <span>{view?.name ?? 'List'}</span>
            {views.length > 1 && <span className="faint">{views.length} views</span>}
            <ChevronDown size={14} />
          </button>
        </Popover>
      </div>
      {search !== null && (
        <div className="m-pad m-lsearch">
          <label className="m-search">
            <Search size={18} />
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks" />
            <button type="button" className="m-icon-btn sm" onClick={() => setSearch(null)} aria-label="Close search">
              <X size={16} />
            </button>
          </label>
        </div>
      )}
      <div className="m-lbody">
        {kind === 'board' && view ? (
          <BoardView view={view} location={location} />
        ) : kind === 'calendar' && view ? (
          <CalendarView view={view} location={location} />
        ) : kind === 'overview' ? (
          <OverviewView location={location} />
        ) : (
          <TaskList location={location} view={view} search={search ?? ''} meOnly={meOnly} />
        )}
      </div>
      <div className="m-lbar">
        <button type="button" className={clsx('m-icon-btn', { on: search !== null })} onClick={() => setSearch((s) => (s === null ? '' : null))} aria-label="Search tasks">
          <Search size={21} />
        </button>
        <button type="button" className={clsx('m-pill', { on: meOnly })} onClick={() => setMeOnly((m) => !m)}>
          <UserCheck size={17} /> {meOnly ? 'Assigned to me' : 'Everyone'}
        </button>
        <span className="spacer" />
        {!(guest && location.type !== 'list') && (
          <button type="button" className="m-fab light" onClick={() => useUi.setState({ createTaskOpen: true })} aria-label="New task">
            <Plus size={24} />
          </button>
        )}
      </div>
    </div>
  );
}
